import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Trash2, Edit3, User, Calendar } from 'lucide-react';
import { authManager, orderNotificationsManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const UserNotesSystem = ({ 
  brandId, 
  orderId,
  orderBrandName,
  onNotesChange, 
  initialNotes = [],
  context = 'brand' // 'brand' or 'order'
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editingText, setEditingText] = useState('');
  const { toast } = useToast();
  
  const currentUser = authManager.getCurrentUser();

  // Use useCallback to prevent onNotesChange from changing on every render
  const handleNotesChange = useCallback((updatedNotes) => {
    if (onNotesChange && typeof onNotesChange === 'function') {
      onNotesChange(updatedNotes);
    }
  }, [onNotesChange]);

  // Only update internal state if initialNotes actually changed (deep comparison)
  useEffect(() => {
    const notesChanged = JSON.stringify(notes) !== JSON.stringify(initialNotes);
    if (notesChanged) {
      setNotes(initialNotes);
    }
  }, [initialNotes]); // Remove notes from dependency to prevent infinite loop

  const addNote = () => {
    if (!newNote.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a note before adding.',
        variant: 'destructive'
      });
      return;
    }

    if (!currentUser) {
      toast({
        title: 'Authentication Error',
        description: 'You must be logged in to add notes.',
        variant: 'destructive'
      });
      return;
    }

    const note = {
      id: Date.now() + Math.random(),
      text: newNote.trim(),
      userId: currentUser.id,
      userName: currentUser.name || currentUser.username,
      userRole: currentUser.role,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toLocaleString('en-IN')
    };

    const updatedNotes = [...notes, note];
    setNotes(updatedNotes);
    setNewNote('');
    
    handleNotesChange(updatedNotes);

    // Trigger notification for order notes
    if (context === 'order' && orderId && orderBrandName) {
      orderNotificationsManager.addOrderNoteNotification(
        orderId,
        orderBrandName,
        updatedNotes.length,
        currentUser.name || currentUser.username,
        note.text
      );
    }

    toast({
      title: 'Note Added',
      description: 'Your note has been added successfully.',
    });
  };

  const editNote = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (note && note.userId === currentUser?.id) {
      setEditingNote(noteId);
      setEditingText(note.text);
    }
  };

  const saveEdit = () => {
    if (!editingText.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Note cannot be empty.',
        variant: 'destructive'
      });
      return;
    }

    const updatedNotes = notes.map(note => 
      note.id === editingNote 
        ? { ...note, text: editingText.trim(), lastEdited: new Date().toLocaleString('en-IN') }
        : note
    );
    
    setNotes(updatedNotes);
    setEditingNote(null);
    setEditingText('');
    
    handleNotesChange(updatedNotes);

    toast({
      title: 'Note Updated',
      description: 'Your note has been updated successfully.',
    });
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setEditingText('');
  };

  const deleteNote = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (note && note.userId === currentUser?.id) {
      const updatedNotes = notes.filter(n => n.id !== noteId);
      setNotes(updatedNotes);
      
      handleNotesChange(updatedNotes);

      toast({
        title: 'Note Deleted',
        description: 'Your note has been deleted successfully.',
      });
    }
  };

  const canEditNote = (note) => {
    return currentUser && note.userId === currentUser.id;
  };

  return (
    <div className="space-y-4">
      {/* Add New Note */}
      <div className="space-y-2">
        <Label htmlFor="newNote">Add Note</Label>
        <Textarea
          id="newNote"
          placeholder="Enter your notes here..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          className="resize-none"
        />
        <Button 
          type="button"
          onClick={addNote}
          className="w-full sm:w-auto"
          disabled={!currentUser}
        >
          Add Note
        </Button>
        {!currentUser && (
          <p className="text-sm text-red-600">You must be logged in to add notes.</p>
        )}
      </div>

      {/* Display Notes */}
      <div className="space-y-3">
        {notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No notes added yet. Be the first to add a note!</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Notes ({notes.length})</h4>
            {notes.map((note) => (
              <Card key={note.id} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span className="font-medium">{note.userName}</span>
                      <span className="text-gray-400">({note.userRole})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>{note.createdAt}</span>
                        {note.lastEdited && (
                          <span className="text-gray-400">(edited: {note.lastEdited})</span>
                        )}
                      </div>
                      {canEditNote(note) && (
                        <div className="flex space-x-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => editNote(note.id)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNote(note.id)}
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {editingNote === note.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="resize-none"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={saveEdit}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserNotesSystem;